// @ts-strict-ignore
import React, {useMemo} from 'react';

import {Segmentation} from 'common/types/reports/query';
import {ButtonSize} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
// eslint-disable-next-line import/no-cycle
import {BucketPaneReact} from 'common/widgets/bucket-pane';
import {getBucketPreview} from 'common/widgets/bucket-pane/numeric-bucket-pane/util';
import {PropertyType} from 'common/report/constants';
import {TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {useQueryContextManager} from 'common/contexts/react/query-context-manager';
import {useTranslation} from 'common/i18n/hooks';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';
import {StringBucket, StringBucketType} from 'common/widgets/bucket-pane/types';

import {ControlSelectEntryPoint} from '../constants';
import {EntryRowProps} from '../row-types/types';
import {Action, EntryPart, isMetricEntry, isSegmentEntry, QueryBucketableEntry} from '../types';
import {getEntryPropertyType, openCustomBucket, usingNewBucketing} from '../row-types/row-util';
import {getCustomizeGroupsTrackingProps, QueryEntrySectionTrackingEvent} from '../tracking';
import {getCustomBucketAttributes} from '../util/group-data';

export function CustomBucketSubitem(
  props: EntryRowProps<QueryBucketableEntry> & {
    onClick?: () => void;
  },
) {
  const queryContextManager = useQueryContextManager();
  const {t, print} = useTranslation();

  const hasTextAnalysis = useFeatureRollout(FeatureRolloutName.TextAnalysis);

  const onMenuChange = (ev: CustomEvent) => {
    if (ev.detail.isOpen) {
      openCustomBucket(props, ControlSelectEntryPoint.CustomBucketSubItem);
    }
  };

  const onChange = (ev: CustomEvent) => {
    const currentCustomBucket = props.entry.customBucket;
    const newCustomBucket = ev.detail;

    props.updateEntry(Action.Edit, {...props.entry, customBucket: newCustomBucket});
    props.setActivePart(EntryPart.CustomBucketing, `close`);

    props.dispatchTrack(QueryEntrySectionTrackingEvent.ApplyCustomBucket, {
      ...props.getEntryTrackingProps(),
      'Bucket min': newCustomBucket.min,
      'Bucket max': newCustomBucket.max,
      'Bucket size': newCustomBucket.bucketSize,
      'Is editing': !!currentCustomBucket,
    });
  };

  const onReset = () => {
    props.updateEntry(Action.Edit, {...props.entry, customBucket: null});
    props.setActivePart(EntryPart.CustomBucketing, `close`);

    props.dispatchTrack(QueryEntrySectionTrackingEvent.ResetCustomBucket, props.getEntryTrackingProps());
  };

  const onUpdateProperty = (ev: CustomEvent) => {
    const segment = ev.detail.segment;

    //TODO: Akshay (Customize Grouping) - add tracking event for updates on "apply" here - one event regardless of prop type (nums 3,4 on notion)
    props.dispatchTrack(QueryEntrySectionTrackingEvent.ApplyCustomBucket, {
      ...getCustomizeGroupsTrackingProps(segment),
      ...props.getEntryTrackingProps(),
    });

    // For some reason customBucket can exist in both the event and outer section, so set them
    // to be the same. TODO make it only exist in the event
    props.updateEntry(Action.Edit, {...props.entry, event: segment, customBucket: segment.customBucket});
  };

  const isOpen = !hasTextAnalysis && props.activePart === EntryPart.CustomBucketing;
  const bucketLabel = useMemo(() => {
    if (!props.entry.event) return null;

    const bucket = props.entry?.customBucket;
    if (usingNewBucketing(props.entry)) {
      // TODO(CC): get custom bucket text for metric entry
      if (isMetricEntry(props.entry)) {
        return null;
      }
      const type = getEntryPropertyType(props.entry);
      const simpleType = (props.entry.event as Segmentation)?.listItemGroup?.propertyType || type.type;
      let bucketText, valid;

      switch (simpleType) {
        case PropertyType.Number:
          valid = !!bucket;
          bucketText = isOpen ? t(`query-builder.buckets.numeric-bucket`) : valid && getBucketPreview(bucket);
          break;
        case PropertyType.String:
        case PropertyType.List:
        case PropertyType.Object: {
          const customGroups: StringBucket = (props.entry.event as any).customGroups;
          let groups = [];

          if (customGroups?.type === StringBucketType.Standard) {
            groups = customGroups.groups.map(
              (group) =>
                group.renamed ?? (Array.isArray(group.filterValue) ? group.filterValue.join(`, `) : group.filterValue),
            );
          } else if (customGroups?.type === StringBucketType.TextAnalysis) {
            groups = customGroups.groups.map((group) => group.name);
          }

          valid = groups.length > 0;

          // In theory the prop values could mess with the markdown but probably okay...
          // Looks like "Bucketing by **renamed; prop1, prop2; prop3**
          bucketText = isOpen
            ? t(`query-builder.buckets.bucket-by`)
            : valid &&
              t(`query-builder.buckets.bucket-by-groups`, {
                groups: groups.join(` | `),
              });
          break;
        }
        case PropertyType.Datetime:
          valid = true;
          bucketText = t(`query-builder.buckets.by-time-period`, {
            timePeriod: print(TimeUnitLabelTranslatable[props.entry.event[`unit`]]?.(1)) || `ISO 8601`,
          });
          break;
        default:
          return null;
      }
      return isOpen || valid ? bucketText : null;
    } else if (bucket) {
      return `Custom bucket size **${bucket.bucketSize} between ${bucket.min}-${bucket.max}**`;
    }
    return null;
  }, [props.entry, isOpen]);

  const bucketAttributes = useMemo(() => {
    if (!props.entry.event) return {};

    if (isMetricEntry(props.entry)) {
      return {};
    }

    return getCustomBucketAttributes({
      entry: props.entry,
      eventNames: props.sectionProps.eventNames || [],
      defaultBucket: isSegmentEntry(props.entry)
        ? queryContextManager.segmentationMetadatas?.[props.index]?.numericBucket
        : undefined,
    });
  }, [props.index, props.entry, props.sectionProps.eventNames]);

  const bucketProps =
    usingNewBucketing(props.entry) && isSegmentEntry(props.entry) ? {segment: props.entry.event as Segmentation} : {};

  const fallbackLabel = hasTextAnalysis
    ? t(`query-builder.actions.group-data`)
    : t(`query-builder.actions.customize-buckets`);

  return bucketLabel || isOpen ? (
    <div className='subitem-text'>
      <SelectReact
        className='subitem-select'
        label={bucketLabel || fallbackLabel}
        open={isOpen}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        preventClose
        onMenuChange={onMenuChange}
        onClick={props.onClick}
      >
        <div slot='body'>
          {isOpen && (
            <BucketPaneReact
              {...bucketAttributes}
              {...bucketProps}
              header={t(`query-builder.actions.customize-range`)}
              onClose={() => props.setActivePart(EntryPart.CustomBucketing, `close`)}
              onUpdateSegment={onUpdateProperty}
              onChangeSelectedBucket={onChange}
              onResetBucket={onReset}
            />
          )}
        </div>
      </SelectReact>
    </div>
  ) : null;
}
